import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardContent, useTheme, useMediaQuery, Button, Checkbox, Tooltip } from '@mui/material';
import { publicInstance } from '../axiosInstance';
import { renderToString } from 'react-dom/server';
import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';
import { alpha } from '@mui/material/styles';


type Shipment = {
  id: number;
  group_id: number;
  recipient: string;
  planned_shipping_date: string;
  comments: string;
  status: string;
  device_type: string;
  quantity: number;
  details: any[];
};

type GroupedShipments = {
  [key: number]: Shipment[];
};

type CompletedShipmentsProps = {
  shipments: Shipment[];
  onUpdateCompletedShipments: (groupId: number) => void;
  onMoveToCurrentShipments: (shipment: Shipment[]) => void;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
};

const groupDetails = (details: any[]) => {
  const groupedByOperator: { [key: string]: string[] } = {};
  const groupedByBoardType: { [key: string]: string[] } = {};

  details.forEach(detail => {
    if (detail.operator) {
      if (!groupedByOperator[detail.operator]) {
        groupedByOperator[detail.operator] = [];
      }
      groupedByOperator[detail.operator].push(Array.isArray(detail.box) ? detail.box.join(', ') : detail.box);
    }

    if (detail.board_type) {
      if (!groupedByBoardType[detail.board_type]) {
        groupedByBoardType[detail.board_type] = [];
      }
      groupedByBoardType[detail.board_type].push(Array.isArray(detail.mac_address) ? detail.mac_address.join(', ') : detail.mac_address);
    }
  });

  return { groupedByOperator, groupedByBoardType };
};

const getBorderStyle = (operator: string | undefined, boardType: string | undefined, isLightTheme: boolean) => {
  if (operator === 'Orange') return '4px solid orange';
  if (operator === 'Play') return '4px solid purple';
  if (operator === 'Plus') return '4px solid green';
  if (boardType === 'p8') return `4px dashed  ${isLightTheme ? 'black' : 'white'}`;
  if (boardType === 'p5') return `4px dotted ${isLightTheme ? 'black' : 'white'}`;
  return `4px solid ${isLightTheme ? 'grey' : 'darkgrey'}`;
};

const CompletedShipments: React.FC<CompletedShipmentsProps> = ({ shipments, onUpdateCompletedShipments, onMoveToCurrentShipments, showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkTheme = theme.name === 'dark';

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  console.log('Selected group IDs:', selectedGroupIds);

  const groupedShipments: GroupedShipments = shipments.reduce((acc: GroupedShipments, shipment: Shipment) => {
    if (!acc[shipment.group_id]) {
      acc[shipment.group_id] = [];
    }
    acc[shipment.group_id].push(shipment);
    return acc;
  }, {});

  const sortedGroupIds = Object.keys(groupedShipments)
    .map(Number)
    .sort((a, b) => b - a);

  const hasShipments = sortedGroupIds.length > 0;

  const handleStatusChange = async (groupId: number, status: string) => {
    try {
      await publicInstance.patch(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`, { status });
      await publicInstance.delete(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`);
      onUpdateCompletedShipments(groupId);
      showSnackbar(`Status updated to ${status} and shipment deleted successfully.`, 'success');
    } catch (error) {
      console.error(`Error updating status to ${status} and deleting shipment:`, error);
      showSnackbar(`Failed to update status to ${status} or delete shipment.`, 'error');
    }
  };

  const toggleSelectionMode = () => {
    setIsSelecting(!isSelecting);
    setSelectedGroupIds([]);
  };

  const handleSelectGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGroupIds.length === sortedGroupIds.length) {
      setSelectedGroupIds([]);
    } else {
      setSelectedGroupIds(sortedGroupIds);
    }
  };

  const handleShipment = async (groupId: number) => {
    try {
      // Get all the box numbers from the shipment details
      const group = groupedShipments[groupId];
      const allDetails = group.flatMap(shipment => shipment.details || []);
      
      // Extract box numbers from devices (sensorboxes and boards)
      const boxNumbers: string[] = [];
      
      allDetails.forEach(detail => {
        // For sensorboxes (devices with operator property)
        if (detail.operator) {
          // Handle both string and array box values
          if (detail.box) {
            if (Array.isArray(detail.box)) {
              boxNumbers.push(...detail.box);
            } else {
              boxNumbers.push(detail.box);
            }
          }
        }
        // Note: We don't update board devices (p5, p8) because they use mac_address, not box
      });
      
      // Filter out any empty strings, null values, or duplicates
      const validBoxNumbers = [...new Set(boxNumbers.filter(box => box))];
      
      console.log('Updating status for boxes:', validBoxNumbers);
      
      // First update the status of all boxes to "WYSŁANY"
      if (validBoxNumbers.length > 0) {
        await publicInstance.patch(
          `/dacapi_sensorbox_introduction_form?box=in.(${validBoxNumbers.join(',')})`, 
          { status: "WYSŁANY" }
        );
      }
      
      // Then update the shipment status and delete it
      await publicInstance.patch(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`, { status: 'wyslane' });
      await publicInstance.delete(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`);
      onUpdateCompletedShipments(groupId);
      showSnackbar('Shipment marked as sent successfully.', 'success');
    } catch (error) {
      console.error('Error marking shipment as sent:', error);
      showSnackbar('Failed to mark shipment as sent.', 'error');
    }
  };

  const handleMoveToCurrentShipments = async (groupId: number, group: Shipment[]) => {
    try {
      await publicInstance.patch(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`, { status: 'utworzone' });
      
      onUpdateCompletedShipments(groupId);
      onMoveToCurrentShipments(group);
      showSnackbar('Shipment moved back to current shipments successfully.', 'success');
    } catch (error) {
      console.error('Error moving shipment to current shipments:', error);
      showSnackbar('Failed to move shipment to current shipments.', 'error');
    }
  };

  const handleCancelShipment = async (groupId: number) => {
    try {
      await publicInstance.patch(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`, { status: 'anulowane' });
      await publicInstance.delete(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`);
      onUpdateCompletedShipments(groupId);
      showSnackbar('Shipment cancelled successfully.', 'success');
    } catch (error) {
      console.error('Error cancelling shipment:', error);
      showSnackbar('Failed to cancel shipment.', 'error');
    }
  };



  const handlePrint = async () => {
    try {
      const selectedShipments = selectedGroupIds.map(id => groupedShipments[id][0]);
  
      // Definicja stylów do wydruku
      const printStyles = `
        @page {
          size: A4;
          margin: 10mm; /* Margines strony */
        }
  
        @media print {
          html, body {
            height: 100%;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
          }
          body {
            visibility: hidden;
          }
  
          /* Główny kontener z treścią do druku */
          #printSection {
            visibility: visible;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            min-height: 100vh;
            overflow: visible;
          }
          #printSection * {
            visibility: visible;
          }
  
          /* Zapobieganie łamaniu się kart w trakcie druku */
          .shipment-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
        }
      `;
  
      // Tworzymy kontener na zawartość do wydruku
      const printDiv = document.createElement('div');
      printDiv.id = 'printSection';
      printDiv.innerHTML = `
        <div style="font-family: Arial, sans-serif; color: black; background-color: white; padding: 10px;">
          <h1 style="text-align: center; margin-bottom: 20px; font-size: 18px;">
            Shipment Details
          </h1>
  
          ${selectedShipments.map(shipment => {
            const { groupedByOperator, groupedByBoardType } = groupDetails(shipment.details);
  
            return `
              <div class="shipment-card" style="border: 1px solid #000; padding: 10px; margin-bottom: 20px; max-width: 210mm; margin: 0 auto; font-size: 11px;">
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
                  <tbody>
                    <tr>
                      <td style="font-weight: bold; padding: 3px; border: 1px solid #ddd; width: 25%;">Group ID</td>
                      <td style="padding: 3px; border: 1px solid #ddd; width: 25%;">${shipment.group_id}</td>
                      <td style="font-weight: bold; padding: 3px; border: 1px solid #ddd; width: 25%;">Recipient</td>
                      <td style="padding: 3px; border: 1px solid #ddd; width: 25%; word-break: break-word;">
                        ${shipment.recipient}
                      </td>
                    </tr>
                    <tr>
                      <td style="font-weight: bold; padding: 3px; border: 1px solid #ddd;">Planned Shipping Date</td>
                      <td style="padding: 3px; border: 1px solid #ddd;">${shipment.planned_shipping_date}</td>
                      <td style="font-weight: bold; padding: 3px; border: 1px solid #ddd;">Status</td>
                      <td style="padding: 3px; border: 1px solid #ddd;">${shipment.status}</td>
                    </tr>
                  </tbody>
                </table>
  
                <div style="display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 10px;">
                  ${Object.entries(groupedByOperator).map(([operator, boxes]) => `
                    <div style="border: 1px solid #ddd; border-radius: 3px; padding: 5px; font-size: 10px;">
                      <strong>${operator}:</strong> ${boxes.length} sensorbox(es)
                    </div>
                  `).join('')}
                  ${Object.entries(groupedByBoardType).map(([boardType, macs]) => `
                    <div style="border: 1px solid #ddd; border-radius: 3px; padding: 5px; font-size: 10px;">
                      <strong>${boardType}:</strong> ${macs.length} device(s)
                    </div>
                  `).join('')}
                </div>
  
                <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                  <div style="flex: 1; min-width: 150px;">
                    <strong>Operators:</strong>
                    ${Object.entries(groupedByOperator).map(([operator, boxes]) => `
                      <div style="margin: 5px 0;">
                        ${operator}: ${boxes.join(', ')}
                      </div>
                    `).join('')}
                  </div>
                  <div style="flex: 1; min-width: 150px;">
                    <strong>Board Types:</strong>
                    ${Object.entries(groupedByBoardType).map(([boardType, macs]) => `
                      <div style="margin: 5px 0;">
                        ${boardType}: ${macs.join(', ')}
                      </div>
                    `).join('')}
                  </div>
                </div>
  
                ${shipment.comments ? `
                  <div style="margin-top: 10px; padding: 5px; border: 1px solid #ddd; border-radius: 3px;">
                    <strong>Comments:</strong> ${shipment.comments}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      `;
  
      // Dodanie stylów do <head>
      const styleElement = document.createElement('style');
      styleElement.innerHTML = printStyles;
      document.head.appendChild(styleElement);
  
      // Dodanie zawartości do <body>
      document.body.appendChild(printDiv);
  
      // Wywołanie okna drukowania
      window.print();
  
      // Sprzątanie po wydruku
      document.body.removeChild(printDiv);
      document.head.removeChild(styleElement);
  
    } catch (error) {
      console.error('Print error:', error);
      showSnackbar('Failed to print. Please try again.', 'error');
    }
  };
  
  

  const handlePrintClick = () => {
    if (selectedGroupIds.length > 0) {
      handlePrint();
    }
  };

  return (
    <Box sx={{ width: isMobile ? '100%' : '48%', display: 'flex', flexDirection: 'column', mb: isMobile ? 4 : 0 }}>
      <Typography variant="h4" gutterBottom>
        Completed Shipments ({sortedGroupIds.length})
      </Typography>
      {hasShipments && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant={isSelecting ? "contained" : "outlined"}
              color={isSelecting ? "secondary" : "primary"}
              onClick={toggleSelectionMode}
              size="small"
              startIcon={isSelecting ? <CancelIcon /> : <PrintIcon />}
            >
              {isSelecting ? 'Cancel Selection' : 'Select for Printing'}
            </Button>

            {isSelecting && (
              <Typography variant="body2" color="textSecondary">
                {selectedGroupIds.length} shipment(s) selected
              </Typography>
            )}
          </Box>
          {isSelecting && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSelectAll}
                size="small"
              >
                {selectedGroupIds.length === sortedGroupIds.length ? 'Deselect All' : 'Select All'}
              </Button>
              {selectedGroupIds.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePrintClick}
                  size="small"
                  startIcon={<PrintIcon />}
                >
                  Print Selected
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        maxHeight: isMobile ? '60vh' : '80vh',
      }}>
        {sortedGroupIds.map((groupId) => {
          const group = groupedShipments[groupId];
          const { groupedByOperator, groupedByBoardType } = groupDetails(group[0].details);
          const isSelected = selectedGroupIds.includes(groupId);

          return (
            <Card
              key={groupId}
              onClick={() => isSelecting && handleSelectGroup(groupId)}
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: 'background.paper',
                border: `1px solid ${theme.palette.borders.primary}`,
                borderRadius: '8px',
                cursor: isSelecting ? 'pointer' : 'default', 
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': isSelecting ? {
                  backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : theme.palette.action.hover,
                  '&::before, &::after': {
                    transform: 'scaleX(0)',
                  },
                } : {},
                '&::before, &::after': isSelecting ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.23)' : theme.palette.primary.main,
                  transition: 'transform 0.3s ease',
                } : {},
                '&::before': {
                  left: 0,
                  transformOrigin: 'left',
                },
                '&::after': {
                  right: 0,
                  transformOrigin: 'right',
                },
              }}
            >
              {isSelecting && (
                <Checkbox
                  checked={isSelected}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    color: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    '&.Mui-checked': {
                      color: isDarkTheme ? theme.palette.primary.light : undefined,
                    },
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleSelectGroup(groupId)}
                />
              )}
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', wordBreak: 'break-word' }}>
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <Typography variant="body2">
                    <strong>Group ID:</strong> {group[0].group_id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Recipient:</strong> {group[0].recipient}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Planned Shipping Date:</strong> {group[0].planned_shipping_date}
                  </Typography>
                  {group.map((item, idx) => (
                    <Typography key={idx} variant="body2">
                      <strong>{item.device_type}:</strong> {item.quantity}
                    </Typography>
                  ))}
                  <Typography variant="body2">
                    <strong>Comments:</strong> {group[0].comments}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {group[0].status}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Details:</strong>
                  </Typography>
                  {Object.keys(groupedByOperator).map((operator, idx) => (
                    <Box key={idx} sx={{ mb: 1, border: getBorderStyle(operator, undefined, !isDarkTheme), borderRadius: '8px', p: 1 }}>
                      <Typography variant="body2">
                        <strong>Operator:</strong> {operator}
                      </Typography>
                      <Typography variant="body2">
                        Boxes: {groupedByOperator[operator].join(', ')}
                      </Typography>
                    </Box>
                  ))}
                  {Object.keys(groupedByBoardType).map((boardType, idx) => (
                    <Box key={idx} sx={{ mb: 1, border: getBorderStyle(undefined, boardType, !isDarkTheme), borderRadius: '8px', p: 1 }}>
                      <Typography variant="body2">
                        <strong>Board Type:</strong> {boardType}
                      </Typography>
                      <Typography variant="body2">
                        MAC Addresses: {groupedByBoardType[boardType].join(', ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {!isSelecting && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '120px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleShipment(group[0].group_id)}
                      fullWidth
                     
                    >
                      Mark as Sent
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleMoveToCurrentShipments(group[0].group_id, group)}
                      fullWidth
                      sx={isDarkTheme ? {
                        backgroundColor: alpha('#b39ddb', 0.9),
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: alpha('#d1c4e9', 1),
                          boxShadow: '0 0 8px rgba(179, 157, 219, 0.6)'
                        },
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(179, 157, 219, 0.5)'
                      } : {}}
                    >
                      Move to Current
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleCancelShipment(group[0].group_id)}
                      fullWidth
                      sx={isDarkTheme ? {
                        backgroundColor: alpha('#f44336', 0.7),
                        '&:hover': {
                          backgroundColor: alpha('#f44336', 0.9),
                          boxShadow: '0 0 8px rgba(244, 67, 54, 0.5)'
                        },
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(244, 67, 54, 0.3)'
                      } : {}}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default CompletedShipments;
